import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import classNames from "classnames/bind";
import {
  addYears,
  eachYearOfInterval,
  endOfDecade,
  endOfYear,
  format,
  isAfter,
  isBefore,
  isSameYear,
  isWithinInterval,
  startOfDecade,
  startOfYear,
  subYears,
} from "date-fns";
import React, { useMemo, useState } from "react";
import { Button } from "../../../Button";
import styles from "../DateSelector.module.scss";
import { NOW } from "../dateSelectorConstants";
import type { RangePickerProps } from "../types";

const cx = classNames.bind(styles);

const YearPicker = ({
  value,
  onChange,
  isRange,
  onClose,
  min: minDate,
  max: maxDate,
  isClearable = false,
}: RangePickerProps) => {
  const [min, setMin] = useState<Date | undefined>();

  const [baseValue, setBaseValue] = useState(
    value ? (value instanceof Date ? value : value?.from) : NOW
  );

  const options = useMemo(() => {
    const minYear = subYears(startOfDecade(baseValue), 1);
    const maxYear = addYears(endOfDecade(baseValue), 1);

    return eachYearOfInterval({ start: minYear, end: maxYear });
  }, [baseValue]);

  const middleValue = useMemo(
    () => options[Math.floor(options.length / 2)],
    [options]
  );

  const onClick = (newValue: Date) => {
    const shouldSetStart =
      !value?.from ||
      !min ||
      isBefore(newValue, min) ||
      (value?.to && isBefore(newValue, value.to)) ||
      (value instanceof Date && isBefore(newValue, value));

    if (shouldSetStart) {
      onChange({
        from: startOfYear(newValue),
        to: isRange ? undefined : endOfYear(newValue),
      });

      if (isRange) {
        setMin(startOfYear(newValue));
      } else {
        onClose();
      }
    } else {
      onChange({ ...value, to: endOfYear(newValue) });

      onClose();
    }
  };

  const onNextOptionsClick = () => {
    setBaseValue(addYears(baseValue, 10));
  };

  const onPreviousOptionsClick = () => {
    setBaseValue(subYears(baseValue, 10));
  };

  return (
    <div className={cx("yearPickerContainer")}>
      <div className={cx("yearPickerHeader")}>
        <button
          type="button"
          className={cx("caretButton")}
          onClick={onPreviousOptionsClick}
        >
          <CaretLeft />
        </button>

        <p className={cx("decadeRange")}>
          {format(options[1], "yyyy")} -{" "}
          {format(options[options.length - 2], "yyyy")}
        </p>

        <button
          type="button"
          className={cx("caretButton")}
          onClick={onNextOptionsClick}
        >
          <CaretRight />
        </button>
      </div>

      <div className={cx("yearPickerCalendar")}>
        {options.map((date) => (
          <button
            type="button"
            key={date.toString()}
            className={cx("calendarItem", "year", {
              isInWindow: isWithinInterval(date, {
                start: startOfDecade(middleValue),
                end: endOfDecade(middleValue),
              }),
              isEndSelected:
                (value?.from && isSameYear(date, value.from)) ||
                (value?.to && isSameYear(date, value.to)),
              ...(isRange
                ? {
                    isSelected:
                      value?.from &&
                      value?.to &&
                      isWithinInterval(date, {
                        start: value?.from,
                        end: value?.to,
                      }),
                  }
                : { isSelected: value?.from && isSameYear(date, value.from) }),
            })}
            onClick={() => onClick(date)}
            disabled={
              (minDate && isBefore(date, minDate)) ||
              (maxDate && isAfter(date, maxDate))
            }
          >
            {format(date, "yyyy")}
          </button>
        ))}
      </div>
      {isClearable && (
        <div className={cx("footer")}>
          <Button
            variant="outline"
            onClick={() => {
              onChange(undefined);
              onClose();
            }}
            className={cx("clear")}
          >
            Clear
          </Button>
        </div>
      )}
    </div>
  );
};

export default YearPicker;
